import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { get } from '@truefit/http-utils';
import { CognitoContext } from '@/features/authentication/components/CognitoProvider';
import { TeamModel } from '@/features/teams/types/TeamModel';

interface SimuliveGamesResult {
  limit: number;
  offset: number;
  total: number;
  results: Array<{
    id: number;
    uniqueId: string;
    name: string;
    startTimestamp: string;
    awayteamid: number;
    hometeamid: number;
    // getAllGames includes homeTeam/awayTeam with no `select`, so the full team
    // rows (abbreviation, teamColors) are already in this payload, and withLogoUrl
    // adds `logoUrl`. Declared now that the table renders the matchup rather
    // than the raw game name; previously they arrived via the index signature.
    awayTeam: TeamModel;
    homeTeam: TeamModel;
    homeTeamColor?: string;
    awayTeamColor?: string;
    status?: string;
    simulatedLive: boolean;
    simulatedLiveStartTime?: string;
    [key: string]: any;
  }>;
}

export const useSimuliveGamesQuery = () => {
  const { cognitoContext } = useContext(CognitoContext);

  return useQuery({
    queryKey: ['simuliveGames'],
    queryFn: async () => {
      const { data } = await get<SimuliveGamesResult>('/v1.0/games?simulatedLive=true&limit=100&offset=0');
      return {
        ...data,
        games: data.results.map((game) => ({
          ...game,
          awayTeamId: game.awayteamid,
          homeTeamId: game.hometeamid,
        })),
      };
    },
    // On a cold reload index.tsx configures http with no Authorization header and
    // Cognito restores the session asynchronously, so an ungated query is rejected
    // by the request interceptor. setHttpAuth runs synchronously before React
    // re-renders with authenticated: true, so this gate is enough to wait it out.
    enabled: cognitoContext.authenticated,
    refetchOnWindowFocus: true,
    staleTime: 60000, // 1 minute
    // The Status column renders game.status, which the scheduler flips
    // recorded -> live mid-replay. Without a poll the row keeps whatever it
    // said at page load and the transition is never visible.
    refetchInterval: 15000,
  });
};
